import _ from 'lodash'
import { DateTime } from 'luxon'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PublicFormContext from '../../contexts/PublicFormContext'
import { flattenEntity } from '../../helpers/entity'
import { sortListWithEntityType } from '../../libraries/entityTools'
import { sanitiseFilterString } from '../../libraries/sanitiseFilterString'

//actions
import entityActions, { getFilterString } from '../../store/entity/actions'
import { evaluateRule } from '../query/evaluateRule'
import RadioWithData from './RadioWithData'
import { useDiscoverEntityEntityType } from '../../hooks/useDiscoverEntityEntityType'
import {
  getAvailableFilters,
  simplifyFilters
} from '../../screens/EntityListScreen'

export const shouldRefetch = (sourceListRoot) => {
  const refetchIfBefore = DateTime.local().minus({ minutes: 5 })
  const lastRetrieved = _.get(sourceListRoot, 'lastRetrieveFinished', null)
  const lastRetrieveStarted = lastRetrieved && DateTime.fromISO(lastRetrieved)
  if (!lastRetrieveStarted || lastRetrieveStarted < refetchIfBefore) {
    return true
  } else {
    return false
  }
}

const _RadioList = (props, ref) => {
  const dispatch = useDispatch()

  const [nextPages, setNextPages] = useState({})

  const sourceNames = useMemo(() => {
    return _.castArray(props.data.field.field_data.params.sourceName)
  }, [props.data.field.field_data.params.sourceName])

  const { entityType } = useDiscoverEntityEntityType(
    null,
    null,
    props.data.field.field_data.params.sourceName,
    null,
    false
  )

  const [serverFilter, setServerFilter] = useState(
    props.data?.field?.field_data?.params?.serverFilter
  )

  useEffect(() => {
    const availableFilters = getAvailableFilters(entityType)
    const newServerFilter = entityType?.object_data?.filters_selected
      ? simplifyFilters(
          {},
          entityType?.object_data?.filters_selected,
          availableFilters
        )
      : props.data?.field?.field_data?.params?.serverFilter

    setServerFilter(newServerFilter)
  }, [entityType])

  const filterRule =
    props?.data?.field?.field_data?.params?.optionsFilter?.json?.logic

  const needsFieldsToSort = useMemo(() => {
    const sortField = _.get(
      entityType,
      'object_data.sort_by_field.field_data.property',
      null
    )

    return !!sortField
  }, [entityType])

  const filter = filterRule || needsFieldsToSort ? { detailed: true } : null

  const publicFormContext = useContext(PublicFormContext)

  const filterString = sanitiseFilterString(
    getFilterString(serverFilter, filter)
  )

  const sourceListRoots = sourceNames.map((entityTypeName) => {
    return useSelector((state) => {
      return entityTypeName
        ? _.get(state.entities, `[${entityTypeName + filterString}]`, {
            data: []
          })
        : []
    })
  })

  const sourceData = useMemo(() => {
    const data = sourceListRoots.map((sourceListRoot) => {
      const data = sortListWithEntityType(
        _.get(sourceListRoot, 'data', []),
        entityType
      )

      return data
    })

    const flattenedData = _.flatten(data)

    const filteredData = filterRule
      ? flattenedData.filter((d) =>
          evaluateRule(filterRule, {
            ...flattenEntity(d, false),
            field: flattenEntity(props.data.entity, false)
          })
        )
      : flattenedData

    return filteredData
  }, [sourceListRoots, filterRule, entityType])

  const fetchNextPage = (index) => {
    if (_.get(nextPages, index, false) !== -1) {
      let pageToFetch

      try {
        pageToFetch = sourceListRoots[index].meta.current_page + 1
      } catch (e) {
        // doesn't matter.
        pageToFetch = nextPages[index]
      }

      if (!pageToFetch || pageToFetch > sourceListRoots[index].meta.last_page) {
        pageToFetch = 1
      }

      dispatch(
        entityActions.listEntity(
          { name: sourceNames[index] },
          {
            filter: {
              ...filter,
              ...serverFilter
            },
            page: pageToFetch,
            detailed: filter?.detailed
          },
          undefined,
          publicFormContext.entityType,
          {
            tenantId: publicFormContext.tenantId,
            tenantHmac: publicFormContext.tenantHmac
          }
        )
      ).then((response) => {
        if (!nextPages[index] || nextPages[index] <= response.meta.last_page) {
          if (response.meta.current_page < response.meta.last_page) {
            setNextPages((nextPages) => ({
              ...nextPages,
              ...{ [index]: response.meta.current_page + 1 }
            }))
          } else {
            setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: -1 } }))
          }
        } else {
          setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: -1 } }))
        }
      })
    }
  }

  useEffect(() => {
    sourceListRoots.map((sourceListRoot, index) => {
      if (shouldRefetch(sourceListRoot)) {
        fetchNextPage(index)
      } else {
        setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: 1 } }))
      }
    })
  }, [serverFilter])

  return (
    <RadioWithData
      {...props}
      dataRows={sourceData}
      labelField={'display_name'}
      value={props.defaultValue}
    />
  )
}
export const RadioList = React.forwardRef(_RadioList)
